import React, { useCallback, useState } from "react"
import { createStyles, View } from "@design-system/core"
import { ChevronLeftIcon, ChevronRightIcon } from "@design-system/icons"
import { LayoutEvent } from "@design-system/hooks"
import { useTabsContext } from "./TabsContext"

export type PreviousNextButtonProps = {
  height: number
  isDisabled?: boolean
  isNext?: boolean
  onClick?: () => void
  onButtonLayout?: (width: number) => void
}

export function PreviousNextButton(props: PreviousNextButtonProps) {
  const { height, isDisabled, isNext, onClick, onButtonLayout } = props
  const [buttonWidth, setButtonWidth] = useState(0)
  const Icon = isNext ? ChevronRightIcon : ChevronLeftIcon

  const handleButtonLayout = useCallback(
    (e: LayoutEvent) => {
      const { width } = e.nativeEvent.layout
      if (width > 0 && width !== buttonWidth) {
        setButtonWidth(width)
        onButtonLayout?.(width)
      }
    },
    [buttonWidth],
  )

  const getButtonTransformStyle = useCallback(
    () => ({
      transform: [
        {
          translateX: 1.2 * buttonWidth * (isNext ? 1 : -1),
        },
      ],
    }),
    [buttonWidth, isNext],
  )

  return (
    <View
      accessibilityLabel={isNext ? "Next" : "Previous"}
      accessibilityRole="button"
      disabled={isDisabled}
      onClick={onClick}
      onLayout={handleButtonLayout}
      style={[
        styles.navButton,
        isNext && styles.navButtonNext,
        { height },
        isDisabled && getButtonTransformStyle(),
      ]}
    >
      <Icon
        style={[
          styles.navButtonIcon,
          isDisabled && styles.navButtonIconDisabled,
        ]}
      />
    </View>
  )
}

const styles = createStyles((theme) => ({
  navButton: {
    backgroundColor: theme.backgroundColors.default,
    paddingHorizontal: theme.spaces.spacing8,
    paddingVertical: theme.spaces.spacing16,
    position: "absolute",
    zIndex: 1,
    transitionProperty: "transform",
    transitionDuration: "0.1s",
    transitionTimingFunction: "ease-out",
  },
  navButtonNext: {
    right: 0,
  },
  navButtonIcon: {
    color: theme.iconColors.interactive,
  },
  navButtonIconDisabled: {
    color: theme.iconColors.disabled,
  },
}))
